import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { appRoutes } from "@/app/router/appRoutes";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { AGENT_SKILL_IMPORT_SEARCH_PARAM } from "@/store/agent-overlay/types";
import {
  acceptSharedAgentSkill,
  getSharedAgentSkill,
  shareAgentSkill,
  unshareAgentSkill,
} from "@/pages/agent/skills/agent-skill-api";

import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonLoadingIndicator,
  MDSButtonSize,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSModalDialog,
  MDSSeparator,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentSkillEditor } from "@/pages/agent/skills/agent-skill-editor";
import { buildAgentSearchableText } from "@/pages/agent/utils/agent-search.utils";
import {
  buildAgentStatusFilterItems,
  formatStatusLabel,
  getAgentStatusIconKind,
} from "@/pages/agent/utils/agent-status.utils";
import type { AgentSkill, CreateAgentSkill } from "@/pages/agent/skills/agent-skill.types";
import { useAppStore } from "@/store";
import { AssistantSkillOrigin, AssistantSkillStatus } from "@/store/assistant";
import { CreateAssistantSkillOperation } from "@/store/sync/operations/assistant-skills/CreateAssistantSkillOperation";
import { DeleteAssistantSkillOperation } from "@/store/sync/operations/assistant-skills/DeleteAssistantSkillOperation";
import { UpdateAssistantSkillOperation } from "@/store/sync/operations/assistant-skills/UpdateAssistantSkillOperation";
import { uuidModule } from "@/modules/uuid";

const ALL_SKILL_STATUSES_FILTER = "all";
const ADAPTATION_HANDOFF_DELAY_MS = 2000;

type StatusFilter = typeof ALL_SKILL_STATUSES_FILTER | AgentSkill["status"];
type SkillOperationError = {
  kind: "delete" | "status";
  message: string;
};
type SkillSaveResult = { saved: true } | { saved: false };

const buildSkillShareUrl = ({ publicId }: { publicId: string }) =>
  windowModule
    .buildUrl({ path: appRoutes.skillImport({ params: { skillImportId: publicId } }).path })
    .toString();

const copyShareUrl = async (url: string) => {
  await copyTextToClipboard({ text: url });
  toastModule.triggerToast({ content: "Link copied to clipboard" });
};

export const AgentSkillsPage = observer(() => {
  const { store } = useAppStore();
  const allSkills = store.assistantSkills.currentAssistantModels;
  const skillsLoaded = store.assistantSkills.currentAssistantModelsLoaded;
  const shareMutation = useMutation({ mutationFn: shareAgentSkill });
  const unshareMutation = useMutation({ mutationFn: unshareAgentSkill });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>(ALL_SKILL_STATUSES_FILTER);
  const [editing, setEditing] = useState<AgentSkill | null>(null);
  const [deleting, setDeleting] = useState<AgentSkill | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [isImportAdapting, setIsImportAdapting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<SkillOperationError | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const importPublicId = searchParams.get(AGENT_SKILL_IMPORT_SEARCH_PARAM);
  const importQuery = useQuery({
    queryKey: ["agent-skill-import", importPublicId],
    queryFn: () => getSharedAgentSkill(importPublicId ?? ""),
    enabled: importPublicId !== null,
    retry: false,
  });
  const importOwnerLabel = importQuery.data?.sharer_display_name ?? "Another Mem user";

  const clearImportParam = () => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(AGENT_SKILL_IMPORT_SEARCH_PARAM);
        return next;
      },
      { replace: true }
    );
    setIsImportAdapting(false);
    setLocalError(null);
  };

  useEffect(() => {
    setIsImportAdapting(false);
    setLocalError(null);
  }, [importPublicId]);

  const hasImportPreview = importQuery.data !== undefined;
  const isImportAlreadySaved = Boolean(importQuery.data?.own_skill_id);
  useEffect(() => {
    if (!hasImportPreview) return;
    trackEvent(TrackedEvent.MemAgentShareLinkView, {
      item_kind: "skill",
      already_saved: isImportAlreadySaved,
    });
  }, [hasImportPreview, isImportAlreadySaved]);

  const isImportUnavailable = importQuery.isError;
  useEffect(() => {
    if (!isImportUnavailable) return;
    trackEvent(TrackedEvent.MemAgentShareLinkUnavailable, { item_kind: "skill" });
  }, [isImportUnavailable]);

  const importedOwnSkillId = importQuery.data?.own_skill_id ?? null;
  useEffect(() => {
    if (!importedOwnSkillId) return;
    if (!skillsLoaded) return;
    const ownSkill = allSkills.find(skill => skill.id === importedOwnSkillId) ?? null;
    if (!ownSkill) return;
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(AGENT_SKILL_IMPORT_SEARCH_PARAM);
        return next;
      },
      { replace: true }
    );
    setEditing(ownSkill);
    setEditorOpen(true);
  }, [allSkills, importedOwnSkillId, setSearchParams, skillsLoaded]);

  const sharingSkill = sharingId ? (allSkills.find(skill => skill.id === sharingId) ?? null) : null;

  const closeShareDialog = () => {
    setSharingId(null);
    shareMutation.reset();
    unshareMutation.reset();
  };

  const skills = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allSkills.filter(skill => {
      const matchesStatus = status === ALL_SKILL_STATUSES_FILTER || skill.status === status;
      const matchesSearch =
        !needle ||
        buildAgentSearchableText({
          values: [skill.name, skill.whenToUse, skill.instructions],
        }).includes(needle);
      return matchesStatus && matchesSearch;
    });
  }, [allSkills, search, status]);

  const statusFilterItems = buildAgentStatusFilterItems({
    allStatusesFilter: ALL_SKILL_STATUSES_FILTER,
    statusFilter: status,
    statusFilterValues: [AssistantSkillStatus.Active, AssistantSkillStatus.Paused],
  });

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setLocalError(null);
  };

  const save = async (value: CreateAgentSkill): Promise<SkillSaveResult> => {
    setIsSaving(true);
    setLocalError(null);
    try {
      if (editing) {
        await new UpdateAssistantSkillOperation({
          store,
          payload: { id: editing.id, ...value },
        }).execute();
        trackEvent(TrackedEvent.MemAgentSkillUpdate, {
          skill_id: editing.id,
          update_kind: "content",
        });
      } else {
        const assistantId = store.assistantSkills.currentAssistantId;
        if (!assistantId) throw new Error("Mem Agent is not ready yet.");
        const savedSkillId = uuidModule.generate();
        await new CreateAssistantSkillOperation({
          store,
          payload: {
            id: savedSkillId,
            assistant_id: assistantId,
            origin: AssistantSkillOrigin.UserCreated,
            ...value,
          },
        }).execute();
        trackEvent(TrackedEvent.MemAgentSkillCreate, { skill_id: savedSkillId, source: "editor" });
      }
      closeEditor();
      return { saved: true };
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : "Unable to save the skill.");
      return { saved: false };
    } finally {
      setIsSaving(false);
    }
  };

  const acceptImportedSkill = async () => {
    if (!importPublicId || !importQuery.data || importQuery.data.own_skill_id) return;

    setIsSaving(true);
    setLocalError(null);
    try {
      const accepted = await acceptSharedAgentSkill(importPublicId);
      if (!accepted.already_accepted) {
        trackEvent(TrackedEvent.MemAgentSkillCreate, {
          skill_id: accepted.accepted_skill_id,
          source: "share_import",
        });
      }
      trackEvent(TrackedEvent.MemAgentShareAccept, { item_kind: "skill" });
      setIsImportAdapting(true);
      window.setTimeout(clearImportParam, ADAPTATION_HANDOFF_DELAY_MS);
    } catch (error) {
      trackEvent(TrackedEvent.MemAgentShareAcceptFailed, { item_kind: "skill" });
      setLocalError(error instanceof Error ? error.message : "Unable to import skill.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async (skill: AgentSkill) => {
    const action = skill.status === AssistantSkillStatus.Active ? "pause" : "resume";
    const nextStatus =
      skill.status === AssistantSkillStatus.Active
        ? AssistantSkillStatus.Paused
        : AssistantSkillStatus.Active;
    setOperationError(null);

    try {
      await new UpdateAssistantSkillOperation({
        store,
        payload: {
          id: skill.id,
          status: nextStatus,
        },
      }).execute();
      trackEvent(TrackedEvent.MemAgentSkillUpdate, { skill_id: skill.id, update_kind: "status" });
    } catch {
      setOperationError({
        kind: "status",
        message: `Unable to ${action} the skill. Please try again.`,
      });
    }
  };

  const deleteSkill = async (skill: AgentSkill) => {
    setOperationError(null);

    try {
      await new DeleteAssistantSkillOperation({ store, payload: { id: skill.id } }).execute();
      trackEvent(TrackedEvent.MemAgentSkillDelete, { skill_id: skill.id });
    } catch {
      setOperationError({
        kind: "delete",
        message: "Unable to delete the skill. Please try again.",
      });
      return;
    }

    setDeleting(null);
  };

  const closeDeleteDialog = () => {
    setDeleting(null);
    setOperationError(null);
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Skills}>
      <StyledHeader>
        <div>
          <MDSText variant={MDSTextVariant.HeaderL}>Personal skills</MDSText>
          <StyledSubtitle>
            Reusable instructions Mem Agent can apply in conversations and routines.
          </StyledSubtitle>
        </div>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onPress={() => {
            setOperationError(null);
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          Create skill
        </MDSButton>
      </StyledHeader>

      <AgentPageFilterBar>
        <AgentFilterSelect
          label="Status"
          selectedKey={status}
          defaultKey={ALL_SKILL_STATUSES_FILTER}
          items={statusFilterItems}
          onSelectionChange={({ key }) => setStatus(key as StatusFilter)}
          onClear={() => setStatus(ALL_SKILL_STATUSES_FILTER)}
        />
        <AgentFilterSearchField
          ariaLabel="Filter skills by name or purpose"
          placeholder="Filter by name or purpose"
          value={search}
          onChange={setSearch}
        />
      </AgentPageFilterBar>

      {!skillsLoaded ? <StyledState>Loading skills…</StyledState> : null}
      {skillsLoaded && skills.length === 0 ? (
        <StyledState>
          {allSkills.length
            ? "No skills match these filters."
            : "No personal skills yet. Create one here or ask Mem Agent to save a workflow."}
        </StyledState>
      ) : null}

      <StyledGrid aria-label="Personal skills" role="list">
        {skills.map(skill => (
          <StyledCardItem
            key={skill.id}
            aria-label={[
              `Skill: ${skill.name}`,
              `Status: ${formatStatusLabel({ status: skill.status })}`,
              `When to use: ${skill.whenToUse}`,
            ].join("; ")}
            role="listitem"
          >
            <StyledCard variant={MDSCardVariant.Chrome}>
              <StyledCardHeader>
                <StyledSkillTitle
                  color={MDSTextColor.Secondary}
                  variant={MDSTextVariant.TextM}
                  weight={MDSTextWeight.Smedium}
                >
                  {skill.name}
                </StyledSkillTitle>

                <StyledStatus>
                  <MDSIcon
                    color={
                      skill.status === AssistantSkillStatus.Active
                        ? MDSIconColor.Positive
                        : MDSIconColor.Secondary
                    }
                    kind={getAgentStatusIconKind({ status: skill.status })}
                  />
                  <MDSText
                    color={
                      skill.status === AssistantSkillStatus.Active
                        ? MDSTextColor.Positive
                        : MDSTextColor.Secondary
                    }
                    variant={MDSTextVariant.TextS}
                  >
                    {formatStatusLabel({ status: skill.status })}
                  </MDSText>
                </StyledStatus>
              </StyledCardHeader>

              <StyledCardSeparator orientation="horizontal" />

              <StyledSkillSection>
                <StyledSectionLabel color={MDSTextColor.Primary} variant={MDSTextVariant.TextS}>
                  When to use:
                </StyledSectionLabel>
                <StyledBody color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
                  {skill.whenToUse}
                </StyledBody>
              </StyledSkillSection>

              <StyledSkillSection>
                <StyledSectionLabel color={MDSTextColor.Primary} variant={MDSTextVariant.TextS}>
                  Instructions:
                </StyledSectionLabel>
                <StyledInstructions
                  color={MDSTextColor.Secondary}
                  variant={MDSTextVariant.ParagraphS}
                >
                  {skill.instructions}
                </StyledInstructions>
              </StyledSkillSection>

              <StyledSkillMetadata>
                <SkillMetadata
                  label="Created by"
                  value={
                    skill.origin === AssistantSkillOrigin.AgentCreated
                      ? "Agent created"
                      : "User created"
                  }
                />
                {skill.publicId ? <SkillMetadata label="Sharing" value="Shared" /> : null}
              </StyledSkillMetadata>

              <StyledActions>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onPress={() => {
                    setOperationError(null);
                    setEditing(skill);
                    setEditorOpen(true);
                  }}
                >
                  Edit
                </MDSButton>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onPress={() => void updateStatus(skill)}
                >
                  {skill.status === AssistantSkillStatus.Active ? "Pause" : "Resume"}
                </MDSButton>
                <MDSButton
                  isDisabled={skill.hasQueuedOperations}
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onPress={() => {
                    trackEvent(TrackedEvent.MemAgentShareDialogOpen, {
                      item_kind: "skill",
                      item_id: skill.id,
                    });
                    setSharingId(skill.id);
                  }}
                >
                  Share
                </MDSButton>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Danger}
                  onPress={() => {
                    setOperationError(null);
                    setDeleting(skill);
                  }}
                >
                  Delete
                </MDSButton>
              </StyledActions>
            </StyledCard>
          </StyledCardItem>
        ))}
      </StyledGrid>

      {operationError?.kind === "status" ? (
        <StyledError role="alert">{operationError.message}</StyledError>
      ) : null}

      <AgentSkillEditor
        skill={editing}
        isOpen={editorOpen}
        isSaving={isSaving}
        error={localError}
        onClose={closeEditor}
        onSave={value => void save(value)}
      />

      <StyledImportModalDialog
        isOpen={
          importPublicId !== null &&
          importQuery.data !== undefined &&
          !importQuery.data.own_skill_id
        }
        handleClose={clearImportParam}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Import shared skill
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          {isImportAdapting ? (
            <StyledCenteredImportStatus aria-live="polite">
              <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Spinner} />
              <MDSText variant={MDSTextVariant.ParagraphS}>
                Mem Agent is going to adapt this skill for you
              </MDSText>
            </StyledCenteredImportStatus>
          ) : (
            <StyledImportContent>
              <StyledImportPreview>
                <StyledImportEyebrow>
                  {importOwnerLabel} shared a Mem Agent skill
                </StyledImportEyebrow>
                <StyledImportTitle>{importQuery.data?.name}</StyledImportTitle>
                <StyledImportSectionLabel>When to use</StyledImportSectionLabel>
                <StyledImportBody>{importQuery.data?.when_to_use}</StyledImportBody>
                <StyledImportSectionLabel>Instructions</StyledImportSectionLabel>
                <StyledImportInstructions>
                  {importQuery.data?.instructions}
                </StyledImportInstructions>
              </StyledImportPreview>
              <StyledImportMessage>Mem Agent adapts imported skills to you</StyledImportMessage>
              {localError ? <StyledError role="alert">{localError}</StyledError> : null}
            </StyledImportContent>
          )}
        </MDSDialogTextContent>
        {!isImportAdapting ? (
          <StyledImportButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
            <MDSButton
              isDisabled={isSaving}
              variant={MDSButtonVariant.Transparent}
              onPress={clearImportParam}
            >
              Not now
            </MDSButton>
            <MDSButton
              isLoading={isSaving}
              loadingIndicator={MDSButtonLoadingIndicator.Icon}
              variant={MDSButtonVariant.Emphasis}
              onPress={() => void acceptImportedSkill()}
            >
              Add skill
            </MDSButton>
          </StyledImportButtonGroup>
        ) : null}
      </StyledImportModalDialog>

      <MDSModalDialog
        isOpen={importPublicId !== null && importQuery.isError}
        handleClose={clearImportParam}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Shared skill unavailable
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            This shared skill is no longer available. The link may have been revoked or the skill
            deleted.
          </MDSText>
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton variant={MDSButtonVariant.Contrast} onPress={clearImportParam}>
            Close
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>

      <MDSModalDialog isOpen={sharingSkill !== null} handleClose={closeShareDialog}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          {sharingSkill ? `Share “${sharingSkill.name}”` : "Share skill"}
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Anyone with this link and a Mem account can save a copy of this skill.
          </MDSText>
          {sharingSkill?.publicId ? (
            <StyledShareUrl>
              {buildSkillShareUrl({ publicId: sharingSkill.publicId })}
            </StyledShareUrl>
          ) : null}
          {shareMutation.error || unshareMutation.error ? (
            <StyledError role="alert">
              Unable to update the share link. Please try again.
            </StyledError>
          ) : null}
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          {sharingSkill?.publicId ? (
            <MDSButton
              isDisabled={unshareMutation.isPending || sharingSkill.hasQueuedOperations}
              variant={MDSButtonVariant.Danger}
              onPress={() => {
                if (!sharingSkill) return;
                unshareMutation.mutate(sharingSkill.id, {
                  onSuccess: () => {
                    trackEvent(TrackedEvent.MemAgentShareLinkRevoke, {
                      item_kind: "skill",
                      item_id: sharingSkill.id,
                    });
                    closeShareDialog();
                  },
                });
              }}
            >
              Revoke link
            </MDSButton>
          ) : null}
          <MDSButton variant={MDSButtonVariant.Transparent} onPress={closeShareDialog}>
            Close
          </MDSButton>
          <MDSButton
            isDisabled={
              sharingSkill === null || sharingSkill.hasQueuedOperations || shareMutation.isPending
            }
            variant={MDSButtonVariant.Contrast}
            onPress={() => {
              if (!sharingSkill) return;
              if (sharingSkill.publicId) {
                trackEvent(TrackedEvent.MemAgentShareLinkCopy, {
                  item_kind: "skill",
                  item_id: sharingSkill.id,
                });
                void copyShareUrl(buildSkillShareUrl({ publicId: sharingSkill.publicId }));
                return;
              }
              shareMutation.mutate(sharingSkill.id, {
                onSuccess: share => {
                  trackEvent(TrackedEvent.MemAgentShareLinkCreate, {
                    item_kind: "skill",
                    item_id: sharingSkill.id,
                  });
                  void copyShareUrl(share.url);
                },
              });
            }}
          >
            {sharingSkill?.publicId
              ? "Copy link"
              : shareMutation.isPending
                ? "Creating…"
                : "Create and copy link"}
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>

      <MDSModalDialog isOpen={deleting !== null} handleClose={closeDeleteDialog}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          {deleting ? `Delete “${deleting.name}”?` : "Delete skill?"}
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Deletion is permanent and cannot be undone.
          </MDSText>
          {operationError?.kind === "delete" ? (
            <StyledError role="alert">{operationError.message}</StyledError>
          ) : null}
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton variant={MDSButtonVariant.Transparent} onPress={closeDeleteDialog}>
            Cancel
          </MDSButton>
          <MDSButton
            isDisabled={deleting === null}
            variant={MDSButtonVariant.DangerFilled}
            onPress={() => {
              if (!deleting) return;
              void deleteSkill(deleting);
            }}
          >
            Delete forever
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>
    </AgentPageLayout>
  );
});

// Keeps skill provenance metadata aligned with the compact routine card metadata row.
const SkillMetadata = ({ label, value }: { label: string; value: string }) => (
  <StyledMetadataItem>
    <MDSText
      color={MDSTextColor.Primary}
      variant={MDSTextVariant.TextS}
      weight={MDSTextWeight.SemiBold}
    >
      {label}:
    </MDSText>
    <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
      {value}
    </MDSText>
  </StyledMetadataItem>
);

const StyledHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;
`;

const StyledSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0;
`;

const StyledGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  height: 100%;
  min-height: 232px;
  min-width: 0;
`;

const StyledCardItem = styled.div`
  min-width: 0;
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledSkillTitle = styled(MDSText)`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const StyledStatus = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledCardSeparator = styled(MDSSeparator)`
  margin: 0;
`;

const StyledSkillSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledSectionLabel = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledBody = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledInstructions = styled(StyledBody)`
  display: -webkit-box;
  overflow: hidden;
  white-space: pre-wrap;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
`;

const StyledSkillMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[5]};
`;

const StyledMetadataItem = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing[3]};
`;

const StyledState = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[8]} 0;
  text-align: center;
`;

const StyledShareUrl = styled.div`
  background: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  overflow-wrap: anywhere;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const StyledImportModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    box-sizing: border-box;
    gap: ${({ theme }) => theme.spacing[3]};
    max-height: calc(100dvh - ${({ theme }) => theme.spacing[8]});
    max-width: calc(100vw - ${({ theme }) => theme.spacing[8]});
    padding: ${({ theme }) => theme.spacing[7]};
    width: min(560px, calc(100vw - ${({ theme }) => theme.spacing[8]}));
  }

  .mds-dialog [slot="header"] {
    min-width: 0;
    overflow-wrap: anywhere;
    padding-block: 0;
  }

  .mds-dialog button[aria-label="Close"] {
    margin: 0 0 0 auto;
  }

  [data-mds-name="dialog-body"] {
    margin-right: ${({ theme }) => `-${theme.spacing[2]}`};
    padding-right: ${({ theme }) => theme.spacing[2]};
  }

  @media (max-width: 560px) {
    .mds-dialog {
      max-height: calc(100dvh - ${({ theme }) => theme.spacing[4]});
      max-width: calc(100vw - ${({ theme }) => theme.spacing[4]});
      padding: ${({ theme }) => theme.spacing[5]};
      width: calc(100vw - ${({ theme }) => theme.spacing[4]});
    }
  }
`;

const StyledImportContent = styled.div`
  min-width: 0;
`;

const StyledImportButtonGroup = styled(MDSButtonGroup)`
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 420px) {
    width: 100%;

    > button {
      flex: 1 1 100%;
      justify-content: center;
    }
  }
`;

const StyledImportPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledImportEyebrow = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StyledImportTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  overflow-wrap: anywhere;
`;

const StyledImportSectionLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-transform: uppercase;
`;

const StyledImportBody = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const StyledImportInstructions = styled(StyledImportBody)`
  white-space: pre-wrap;
`;

const StyledImportMessage = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const StyledCenteredImportStatus = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
  min-height: 160px;
  text-align: center;
`;
